import {
  CameraBurstBufferResponseDTO,
  CameraBurstResponseDTO, CameraConnectivityResponseDTO,
  CameraDisplayResponseDTO,
  CameraStorageDataResponseDTO, CameraVideoItemResponseDTO,
  CameraViewfinderResponseDTO, LensFocalResponseDTO,
  ProductResponseDTO
} from "@shared/DTO";
import { SpecsItem } from "@/scripts/modules/ab-data-sheet/types/specifications.types";
import {
  DisplayMoveabilityEnum, FocalTypeEnum,
  Mount,
  ProductTypeEnum,
  SensorType,
  VideoFormatEnum,
  ViewfinderTypeEnum
} from "@shared/enums";
import { SpecsCompareTypeEnum } from "@/scripts/modules/ab-data-sheet/specifications.enums";
import { CanonMounts, getMountDefinition, MOUNT_CATALOG, NikonMounts, SonyMounts } from "@shared/values";
import {
  Canon_Lens_Guide, CFexpress_TypeA_Card_Amazon_Link, CFexpress_TypeB_Card_Amazon_Link,
  Nikon_Lens_Guide,
  SD_Card_Amazon_Link,
  Sony_Lens_Guide
} from "@/scripts/modules/ab-data-sheet/constants/lnks";


export class AbProductSpecifications {

  private products: ProductResponseDTO[];
  public compareType: SpecsCompareTypeEnum;
  public items: SpecsItem[];

  constructor(products: ProductResponseDTO[]) {
    this.products = products;
    this.setCompareType();
    this.items = this.composeSpecsItems();
    return this;
  }

  private setCompareType(): void {

    const productType = (product: ProductResponseDTO): ProductTypeEnum => {
      return product.productData.productType;
    }

    const allLenses = this.products
      .every((product) =>
        productType(product) === ProductTypeEnum.OBIETTIVO
      );
    if (allLenses) {
      this.compareType = SpecsCompareTypeEnum.LENS;
      return;
    }

    const allInterchangeableLensCameras = this.products
      .every((product) =>
        [ProductTypeEnum.MIRRORLESS, ProductTypeEnum.REFLEX]
          .includes(productType(product))
      );
    if (allInterchangeableLensCameras) {
      this.compareType = SpecsCompareTypeEnum.CAMERA_INTERCHANGEABLE_LENS;
      return;
    }

    const allFixedLensCameras = this.products
      .every((product) =>
        [ProductTypeEnum.COMPATTA, ProductTypeEnum.BRIDGE]
          .includes(productType(product))
      );
    if (allFixedLensCameras) {
      this.compareType = SpecsCompareTypeEnum.CAMERA_FIXED_LENS;
      return;
    }
  }

  private composeSpecsItems(): SpecsItem[] {
    if (this.compareType === SpecsCompareTypeEnum.CAMERA_INTERCHANGEABLE_LENS) {
      return [
        this.getProductTypeItem(),
        this.getSensorTypeItem(),
        this.getSensorResolution(),
        this.getIsoItem(),
        this.getMountItem(),
        this.getCameraFocusItem(),
        this.getDisplayItem(),
        this.getViewfinderItem(),
        this.getDriveItem(),
        this.getCameraModesItem(),
        this.getFileTypeItem(),
        this.getVideoItem(),
        this.getWifiItem(),
        this.getBluetoothItem(),
        this.getGpsItem(),
        this.getNfcItem(),
        this.getStabilizerItem(),
        this.getStorageItem(),
        this.getSealedItem(),
        this.getSizesItem(),
        this.getWeightItem(),
      ];
    }

    if (this.compareType === SpecsCompareTypeEnum.CAMERA_FIXED_LENS) {      
      return [
        this.getProductTypeItem(),
        this.getSensorTypeItem(),
        this.getSensorResolution(),
        this.getIsoItem(),
        this.getZoomItem(),
        this.getFocalsItem(),
        this.getLensAutofocusItem(),
        this.getLensManualFocusItem(),
        this.getMinFocusDistanceItem(),
        this.getMaxMagnificationItem(),
        this.getApertureMaxItem(),
        this.getApertureMinItem(),
        this.getFiltersDiameterItem(),
        this.getDisplayItem(),
        this.getViewfinderItem(),
        this.getDriveItem(),
        this.getCameraModesItem(),
        this.getFileTypeItem(),
        this.getVideoItem(),
        this.getWifiItem(),
        this.getBluetoothItem(),
        this.getGpsItem(),
        this.getNfcItem(),
        this.getStabilizerItem(),
        this.getStorageItem(),
        this.getSealedItem(),
        this.getSizesItem(),
        this.getWeightItem(),
      ];
    }

    if (this.compareType === SpecsCompareTypeEnum.LENS) {      
      return [
        this.getSubCategoryItem(),
        this.getFocalsItem(),
        this.getZoomItem(),
        this.getApertureMaxItem(),
        this.getApertureMinItem(),
        this.getLensMountsItem(),
        this.getLensAutofocusItem(),
        this.getLensManualFocusItem(),
        this.getMinFocusDistanceItem(),
        this.getMaxMagnificationItem(),
        this.getFiltersDiameterItem(),        
        this.getStabilizerItem(),
        this.getSealedItem(),
        this.getSizesItem(),
        this.getWeightItem(),
      ];
    }

    return [];
  }

  private createSpecsItem<T>(
    label: string,
    mapProductToValue: (product: ProductResponseDTO) => { id: string; value?: T; html: string },
    isValidValue: (value: T) => boolean,
    findBestValue?: (values: T[]) => T,
    isBestValue?: (value: T, best: T) => boolean
  ): SpecsItem {
    const allItems = this.products.map(mapProductToValue);
    const validValues = allItems.map(i => i.value).filter(v => v !== undefined && v !== null && isValidValue(v));

    if (validValues.length === 0) return null;

    const specsItem: SpecsItem = {
      label,
      values: allItems.map(({ id, html }) => ({ id, html })),
      bestProducts: null
    };

    if (findBestValue && isBestValue && validValues.length > 0) {
      const best = findBestValue(validValues);
      const bestProductIds = allItems
        .filter(i => i.value !== undefined && i.value !== null && isBestValue(i.value, best))
        .map(i => i.id);

      if (bestProductIds.length < this.products.length) {
        specsItem.bestProducts = bestProductIds;
      }
    }

    return specsItem;
  }

  private getProductTypeItem(): SpecsItem {
    return this.createSpecsItem<ProductTypeEnum>(
      'tipologia',
      (product) => {
        const type = product.productData.productType;
        let html = '';
        switch (type) {
          case ProductTypeEnum.BRIDGE: html = 'Fotocamera bridge'; break;
          case ProductTypeEnum.COMPATTA: html = 'Fotocamera compatta'; break;
          case ProductTypeEnum.MIRRORLESS: html = 'Fotocamera mirrorless'; break;
          case ProductTypeEnum.REFLEX: html = 'Fotocamera reflex'; break;
          case ProductTypeEnum.OBIETTIVO: html = 'Obiettivo fotografico'; break;
        }
        return { id: product._id, value: type, html };
      },
      (val) => !!val
    );
  }

  private getSensorTypeItem(): SpecsItem {
    const sensorTypeMap = [
      { type: SensorType.FULL_FRAME, value: 1 },
      { type: SensorType.APS_C, value: 2 },
      { type: SensorType.QUATTRO_TERZI, value: 3 },
      { type: SensorType.I_1, value: 4 },
      { type: SensorType.I_1_23, value: 5 },
    ];
    const getSensorValue = (outerType: string) => sensorTypeMap.find(({ type }) => type === outerType)?.value || 100;

    return this.createSpecsItem<string>(
      'tipo sensore',
      ({ cameraData, _id }) => {
        const sensorType = cameraData?.sensor?.sensorType ?? '';
        return { id: _id, value: sensorType, html: sensorType };
      },
      (val) => !!val?.length,
      (values) => {
        const minVal = Math.min(...values.map(getSensorValue));
        return sensorTypeMap.find(x => x.value === minVal)?.type;
      },
      (val, best) => val === best
    );
  }

  private getSensorResolution(): SpecsItem {
    return this.createSpecsItem<number>(
      'risoluzione',
      ({ cameraData, _id }) => {
        const resolution = Number(cameraData?.sensor?.sensorResolution) || null;
        const html = resolution ? `${resolution} megapixel` : '';
        return { id: _id, value: resolution, html };
      },
      (val) => !!val,
      (values) => Math.max(...values),
      (val, best) => val === best
    );
  }

  private getIsoItem(): SpecsItem {
    return this.createSpecsItem<number>(
      'sensibilità ISO',
      ({ cameraData, _id }) => {
        const isoRows = cameraData?.isoSensitivity;
        const maxIso = isoRows?.at(0)?.isoMax;
        const html = isoRows?.map(iso => {
          if (!iso) return null;
          const { isoMin, isoMax, notes } = iso;
          return `<p>Da ${isoMin} a ${isoMax} ISO ${notes?.length ? '(' + notes + ')' : ''}</p>`;
        }).join('') || '';
        return { id: _id, value: maxIso, html };
      },
      (val) => !!val,
      (values) => Math.max(...values),
      (val, best) => val === best
    );
  }

  private getMountItem(): SpecsItem {
    return this.createSpecsItem<string>(
      'innesto obiettivi',
      ({ cameraInterchangeableLensData, lensData, _id }) => {
        const cameraMount = cameraInterchangeableLensData?.mount;
        const mountIds = cameraMount
          ? [cameraMount]
          : (lensData?.mounts ?? []).map(({mount}) => mount);
        const mountNames = mountIds
          .map((mount) => getMountDefinition(mount)?.name)
          .filter(Boolean);
        let html = mountNames.join(', ');
        if (cameraMount) {
          const link = this.getGuidesForMount(cameraMount);
          if (link?.length) {
            html += `<div><a href="${link}" target="_blank">Vedi guida obiettivi compatibili</a></div>`;
          }
        }
        return { id: _id, value: mountIds.join(','), html };
      },
      (val) => val.length > 0
    );
  }

  private getLensMountsItem(): SpecsItem {
    return this.createSpecsItem<string[]>(
      'Mount Compatibili',
      ({ lensData, _id }) => {
        const value = lensData.mounts.map(m => m.mount);
        
        const htmlLi = value.map( mount => {
          const {name, description} = MOUNT_CATALOG[mount];
          return `<li><strong>${name}</strong>: ${description}</li>`
        })
        .join("");
        const html = `
          <ul>
            ${htmlLi}        
            <a href='javascript:void(null)'
               ab-hot-link='open-lens-compatibility'
               ab-hot-link-options='{ "productid": "${_id}"}'>
                Verifica compatibilità fotocamera
            </a>
          </ul>`;
        return { id: _id, value, html };
      },
      (val) => val.length > 0
    )
  }

  

  private getGuidesForMount(cameraMount: Mount): string {
    if (CanonMounts.includes(cameraMount)) return Canon_Lens_Guide;
    if (NikonMounts.includes(cameraMount)) return Nikon_Lens_Guide;
    if (SonyMounts.includes(cameraMount)) return Sony_Lens_Guide;
    return null;
  }

  private getCameraFocusItem(): SpecsItem {
    return this.createSpecsItem<boolean>(
      'autofocus',
      ({ cameraData, _id }) => {
        const hasAf = cameraData?.autofocus?.hasIt;
        const notes = cameraData?.autofocus?.notes;
        const html = `${hasAf ? 'Sì' : 'No'} ${!notes?.length ? '' : notes}`;
        return { id: _id, value: hasAf, html };
      },
      (val) => val !== undefined
    );
  }

  private getDisplayItem(): SpecsItem {
    const formatDisplayHtml = (display: CameraDisplayResponseDTO) => {
      let html = '';
      if (display.size && display.size > 0) html += `<p>Diagonale: ${display.size}"</p>`;
      if (display.resolution && display.resolution > 0) html += `<p>Risoluzione: ${display.resolution}px</p>`;
      if (display.moveability && [DisplayMoveabilityEnum.FIXED, DisplayMoveabilityEnum.ARTICULATING, DisplayMoveabilityEnum.FLIPABLE].includes(display.moveability)) {
        const labels = {
          [DisplayMoveabilityEnum.FIXED]: 'Fisso',
          [DisplayMoveabilityEnum.ARTICULATING]: 'Completamente articolato',
          [DisplayMoveabilityEnum.FLIPABLE]: 'Orientabile',
          [DisplayMoveabilityEnum.UNKNOWN]: ''
        };
        const label = labels[display.moveability];
        if (label) html += `<p>Rotazione: ${label}</p>`;
      }
      html += `<p>Touchscreen: ${display?.displayTouch ? 'Sì' : 'No'}</p>`;
      if (display?.additionalInfo?.length) html += `<p>${display.additionalInfo}</p>`;
      return html;
    };

    return this.createSpecsItem<number>(
      'display',
      ({ cameraData, _id }) => {
        const display = cameraData?.display;
        const html = formatDisplayHtml(display);
        const size = display?.size;
        return { id: _id, value: size, html };
      },
      (val) => !!val && val > 0,
      (values) => Math.max(...values),
      (val, best) => val === best
    );
  }

  private getViewfinderItem(): SpecsItem {
    const formatViewfinderHtml = (viewfinder: CameraViewfinderResponseDTO) => {
      if (!viewfinder?.type && !viewfinder?.notes?.length) return 'Non presente';
      let html = '';
      if ([ViewfinderTypeEnum.ELECTRONIC, ViewfinderTypeEnum.OPTICAL].includes(viewfinder.type)) {
        html += `<p>Tipo: ${viewfinder.type === ViewfinderTypeEnum.ELECTRONIC ? 'elettronico' : 'ottico'}</p>`;
      }
      if (viewfinder.size) html += `<p>Diagonale: ${viewfinder.size}"</p>`;
      if (viewfinder.resolution) html += `<p>Risoluzione: ${viewfinder.resolution}px</p>`;
      if (viewfinder.coverage) html += `<p>Copertura: ${viewfinder.coverage}%</p>`;
      if (viewfinder.magnification) html += `<p>Ingrandimento: ${viewfinder.magnification}x</p>`;
      if (viewfinder.notes) html += `<p>${viewfinder.notes}</p>`;
      return html;
    };

    return this.createSpecsItem<boolean>(
      'mirino',
      ({ cameraData, _id }) => {
        const viewfinder = cameraData?.viewfinder;
        const html = formatViewfinderHtml(viewfinder);
        const hasViewfinder = !!viewfinder;
        return { id: _id, value: hasViewfinder, html };
      },
      (val) => true,
      (values) => true,
      (val, best) => val === true
    );
  }

  private getDriveItem(): SpecsItem {
    const formatBufferRules = (bufferRules: CameraBurstBufferResponseDTO[]) => {
      if (!bufferRules?.length) return '';
      return bufferRules.map(rule => {
        const { maxBuffer, bufferNotes, bufferFormat } = rule;
        const types = bufferFormat?.map(t => t.toUpperCase());
        let h = '';
        if (maxBuffer > 0) h += `fino a ${maxBuffer} scatti`;
        if (types?.length) {
          h += ' in formato ';
          if (types.length === 1) h += types[0];
          else if (types.length === 2) h += types.join(' e ');
          else h += `${types.slice(0, -1).join(', ')} e ${types.at(-1)}`;
        }
        if (bufferNotes) h += ` ${bufferNotes}`;
        return h;
      }).filter(h => h.length).join(' o ');
    };

    const formatBurst = (burst: CameraBurstResponseDTO[]) => {
      if (!burst?.length) return '';
      return burst.map(b => {
        if (!b.fps && !b.notes) return null;
        let h = '';
        if (b.fps) h += `${b.fps} fps ${formatBufferRules(b.buffer)}`;
        if (b.notes) h += ` ${b.notes}`;
        return `<p>${h}</p>`;
      }).join('');
    };

    return this.createSpecsItem<number>(
      'scatto continuo',
      ({ cameraData, _id }) => {
        const burst = cameraData?.burst;
        const html = formatBurst(burst);
        let rating = 0;
        if (burst?.length) {
          const fps = burst[0]?.fps || 0;
          const minBuffer = Math.min(...(burst[0]?.buffer?.map(b => b.maxBuffer ?? 0) || [0]));
          rating = (fps * 1000) + minBuffer;
        }
        return { id: _id, value: rating, html };
      },
      (val) => val > 0,
      (values) => Math.max(...values),
      (val, best) => val === best
    );
  }

  private getCameraModesItem(): SpecsItem {
    return this.createSpecsItem<number>(
      'modalità di scatto',
      ({ cameraData, _id }) => {
        const modes = cameraData?.modes;
        const items = [];
        if (modes?.auto?.hasIt) items.push(`Automatiche${modes.auto.notes?.length ? '(' + modes.auto.notes + ')' : ''}`);
        if (modes?.manual?.hasIt) items.push(`Manuali${modes.manual.notes?.length ? '(' + modes.manual.notes + ')' : ''}`);
        const html = items.join(' e ');
        return { id: _id, value: items.length, html };
      },
      (val) => val > 0,
      (values) => Math.max(...values),
      (val, best) => val === best
    );
  }

  private getFileTypeItem(): SpecsItem {
    return this.createSpecsItem<number>(
      'formati immagine',
      ({ cameraData, _id }) => {
        const formats = [];
        if (cameraData?.filesFormats?.jpeg?.hasIt) formats.push('JPEG');
        if (cameraData?.filesFormats?.raw?.hasIt) formats.push('RAW');
        formats.sort();
        let html = '';
        if (formats.length === 1) html = formats[0];
        else if (formats.length === 2) html = formats.join(' e ');
        else if (formats.length > 2) html = `${formats.slice(0, -1).join(', ')} e ${formats.at(-1)}`;
        return { id: _id, value: formats.length, html };
      },
      (val) => val > 0,
      (values) => Math.max(...values),
      (val, best) => val === best
    );
  }

  private getVideoItem(): SpecsItem {
    const formatVideoRowAsHtml = (videoItem: CameraVideoItemResponseDTO): string => {
      const itemsToPrint = [];
      if (videoItem?.format?.length) itemsToPrint.push(`Formato: ${videoItem.format?.toUpperCase()}`);
      if (videoItem?.resolution) itemsToPrint.push(`Risoluzione: ${videoItem.resolution}`);
      if (videoItem?.fps) itemsToPrint.push(`${videoItem.fps.toString()}fps`);
      if (videoItem?.notes?.length) itemsToPrint.push(`Note: ${videoItem.notes}`);
      return itemsToPrint?.length ? itemsToPrint.join(' - ') : null;
    };

    const videoFormatRatingMap = [
      { type: VideoFormatEnum.VIDEO_NOT_SUPPORTED, value: 0 },
      { type: VideoFormatEnum.VIDEO_HD, value: 10 },
      { type: VideoFormatEnum.VIDEO_FULL_HD, value: 20 },
      { type: VideoFormatEnum.VIDEO_4K, value: 30 },
      { type: VideoFormatEnum.VIDEO_C4K, value: 30 },
      { type: VideoFormatEnum.VIDEO_6K, value: 40 },
      { type: VideoFormatEnum.VIDEO_C6K, value: 40 },
      { type: VideoFormatEnum.VIDEO_6_2K, value: 50 },
      { type: VideoFormatEnum.VIDEO_8K, value: 60 },
    ];

    const getVideoFormatRating = (videoFormat: VideoFormatEnum) =>
      videoFormatRatingMap.find(({ type }) => type === videoFormat)?.value || 0;

    return this.createSpecsItem<number>(
      'formato video',
      ({ cameraData, _id }) => {
        const videoItems = cameraData?.video;
        const html = videoItems?.map((videoItem) => `<p>${formatVideoRowAsHtml(videoItem)}</p>`).join('') || '';
        const supportedKnownFormats = videoItems
          ?.map((videoItem) => videoItem.format)
          .filter((format) => !!format) || [];
        const bestSupportedFormat = Math.max(...supportedKnownFormats.map((format) => getVideoFormatRating(format)), 0);
        return { id: _id, value: bestSupportedFormat, html };
      },
      (val) => val > 0,
      (values) => Math.max(...values),
      (val, best) => val === best
    );
  }

  private getWifiItem(): SpecsItem {
    return this.getCameraConnectivityItem('wifi', 'wifi', true);
  }

  private getBluetoothItem(): SpecsItem {
    return this.getCameraConnectivityItem('bluetooth', 'bluetooth', true);
  }

  private getGpsItem(): SpecsItem {
    return this.getCameraConnectivityItem('gps', 'GPS', true);
  }

  private getNfcItem(): SpecsItem {
    return this.getCameraConnectivityItem('nfc', 'NFC', true);
  }

  private getStabilizerItem(): SpecsItem {
    const caluclateIsRating = (known: boolean, hasIs: boolean, isStops: number) => !known || !hasIs ? 0 : 100 + (isStops || 0);

    return this.createSpecsItem<{ known: boolean; hasIs: boolean; isStops: number }>(
      'stabilizzatore',
      (product) => {
        const stabilizerItem = product.cameraData?.stabilizer || product.lensData?.stabilizer;
        const known = !!stabilizerItem;
        const hasIs = known ? stabilizerItem.hasIt : false;
        const isStops = known ? stabilizerItem.stops : 0;
        const html = !known ? null : `${hasIs ? 'Sì' : 'No'} ${isStops ? ('(' + isStops + ' Stop' + ')') : ''}`;
        return { id: product._id, value: { known, hasIs, isStops }, html };
      },
      (val) => val.known,
      (values) => {
        const maxRating = Math.max(...values.map(v => caluclateIsRating(v.known, v.hasIs, v.isStops)));
        return { known: true, hasIs: true, isStops: maxRating - 100 };
      },
      (val, best) => caluclateIsRating(val.known, val.hasIs, val.isStops) === caluclateIsRating(best.known, best.hasIs, best.isStops)
    );
  }

  private getStorageItem(): SpecsItem {
    const getFormattedCardTypeName = (cardsTypes: string[]) => cardsTypes?.join('/')?.toUpperCase();
    const formatStorageSlotAsHtml = (slot: CameraStorageDataResponseDTO) => {
      const cardsTypes = slot?.supportedTypes;
      const notes = slot?.notes;
      if (!cardsTypes?.length && !notes?.length) return '';
      return `${getFormattedCardTypeName(cardsTypes)} ${notes?.length ? (' ' + notes) : ''}`;
    };
    const getCardAmazonLink = (cardType: string) => {
      const cardTypeNormalized = cardType?.toUpperCase();
      let link = null;
      if (cardTypeNormalized?.includes('SD')) link = SD_Card_Amazon_Link;
      else if (cardTypeNormalized?.includes('CF EXPRESS TIPO B')) link = CFexpress_TypeB_Card_Amazon_Link;
      else if (cardTypeNormalized?.includes('CF EXPRESS TYPE A')) link = CFexpress_TypeA_Card_Amazon_Link;
      return link ? ` <a rel="nofollow" href="${link}" target="_blank">Acquista su Amazon</a>` : null;
    };

    return this.createSpecsItem<{ hasMultipleSlots: boolean }>(
      'memoria',
      ({ cameraData, _id }) => {
        const storage = cameraData?.storage;
        const hasMultipleSlots = storage?.length > 1;
        const allSlotSameType = storage?.every(slot => getFormattedCardTypeName(slot?.supportedTypes) === getFormattedCardTypeName(storage[0]?.supportedTypes));
        const amazonLinkForEachSlot = !hasMultipleSlots || !allSlotSameType;
        let html = storage?.map((slot, index, selfArray) => {
          const prefix = selfArray.length > 1 ? `Slot ${index + 1}: ` : '';
          const link = amazonLinkForEachSlot ? getCardAmazonLink(slot.supportedTypes[0]) : null;
          return `<p>${prefix}${formatStorageSlotAsHtml(slot)}${link || ''}</p>`;
        }).join('') || '';
        if (!amazonLinkForEachSlot) {
          const link = getCardAmazonLink(storage[0].supportedTypes[0]);
          if (link) html += `<div>${link}</div>`;
        }
        return { id: _id, value: { hasMultipleSlots }, html };
      },
      (val) => true,
      (values) => ({ hasMultipleSlots: true }),
      (val, best) => val.hasMultipleSlots
    );
  }

  private getSealedItem(): SpecsItem {
    return this.createSpecsItem<boolean>(
      'tropicalizzazione',
      ({ productData, _id }) => {
        const sealing = productData?.sealing;
        const known = !!sealing;
        const isSealed = sealing?.hasIt;
        const html = !known ? '' : isSealed ? 'Sì' : 'No';
        return { id: _id, value: isSealed, html };
      },
      (val) => val !== undefined,
      (values) => true,
      (val, best) => val === true
    );
  }

  private getSizesItem(): SpecsItem {
    const getProductVolumeBySizes = (sizes: number[]) => !sizes?.length ? null : sizes.reduce((a, b) => a * b);

    return this.createSpecsItem<number[]>(
      'dimensioni',
      (product) => {
        const sizes = product.productData.sizes?.length >= 2 ? product.productData.sizes : [];
        const html = sizes.length < 2 ? '' : `${sizes.map((size) => size.toString() + 'mm').join(' x ')}`;
        return { id: product._id, value: sizes, html };
      },
      (val) => val?.length >= 2,
      (values) => {
        const volumes = values.map(getProductVolumeBySizes).filter(v => v);
        const minVol = Math.min(...volumes);
        return values.find(v => getProductVolumeBySizes(v) === minVol);
      },
      (val, best) => getProductVolumeBySizes(val) === getProductVolumeBySizes(best)
    );
  }

  private getWeightItem(): SpecsItem {
    return this.createSpecsItem<number>(
      'peso',
      (product) => {
        const weight = product.productData?.weight || null;
        const html = !weight ? null : `${weight} grammi`;
        return { id: product._id, value: weight, html };
      },
      (val) => !!val && val > 0,
      (values) => Math.min(...values),
      (val, best) => val === best
    );
  }

  private getZoomItem(): SpecsItem {
    return this.createSpecsItem<number>(
      'zoom',
      (product) => {
        const focals = product.lensData?.focals || product?.cameraFixedLensData.focals;
        const { min, max } = focals?.at(0) ?? {};
        let zoomValue = (max && min) ? Number(max / min) : null;
        const html = zoomValue ? (Number.isInteger(zoomValue) ? `${zoomValue}x` : `${zoomValue.toFixed(2).replace(/\.?0+$/, '')}x`) : '';
        return { id: product._id, value: zoomValue, html };
      },
      (val) => !!val,
      (values) => Math.max(...values),
      (val, best) => val === best
    );
  }

  private getSubCategoryItem(): SpecsItem {
    return this.createSpecsItem<string>(
      'categoria',
      (product) => {
        const value = product.productData.subCategory?.join(', ') ?? '';
        return { id: product._id, value, html: value };
      },
      (val) => val.length > 0
    );
  }

  private getFocalsItem(): SpecsItem {
    const formatFocalsAsHtml = (focals: LensFocalResponseDTO[]) => {
      if (!focals?.length) return '';
      return focals.map((focal) => {
        if (!focal.min && !focal.max) return null;
        let html = `<p>${focal.min}`;
        if (focal.min !== focal.max) html += `-${focal.max}`;
        html += `mm${focal.type === FocalTypeEnum.EQUIVALENT ? ' equivalenti' : ''}</p>`;
        return html;
      }).filter(h => h?.length).join('');
    };

    return this.createSpecsItem<LensFocalResponseDTO[]>(
      'lunghezza focale',
      (product) => {
        const focals = product.cameraFixedLensData?.focals ?? product.lensData?.focals;
        const html = !focals?.length ? '' : formatFocalsAsHtml(focals);
        return { id: product._id, value: focals, html };
      },
      (val) => !!val?.length
    );
  }

  private getLensAutofocusItem(): SpecsItem {
    return this.createSpecsItem<boolean>(
      'autofocus',
      (product) => {
        const focus = product?.cameraFixedLensData?.lensFocus || product.lensData.lensFocus;
        const hasAf = focus?.autoFocus?.hasIt;
        const html = !focus?.autoFocus ? '' : `${hasAf ? 'Sì' : 'No'} ${focus.autoFocus?.notes?.length ? ('(' + focus.autoFocus.notes + ')') : ''}`;
        return { id: product._id, value: hasAf, html };
      },
      (val) => val !== undefined,
      (values) => true,
      (val, best) => val === true
    );
  }

  private getLensManualFocusItem(): SpecsItem {
    return this.createSpecsItem<boolean>(
      'Messa a fuoco manuale',
      (product) => {
        const focus = product?.cameraFixedLensData?.lensFocus || product.lensData.lensFocus;
        const hasMf = focus?.manualFocus?.hasIt;
        const html = !focus?.manualFocus ? '' : `${hasMf ? 'Sì' : 'No'}`;
        return { id: product._id, value: hasMf, html };
      },
      (val) => val !== undefined,
      (values) => true,
      (val, best) => val === true
    );
  }

  private getMinFocusDistanceItem(): SpecsItem {
    return this.createSpecsItem<number>(
      'distanza minima di messa a fuoco',
      (product) => {
        const focus = product?.cameraFixedLensData?.lensFocus || product.lensData.lensFocus;
        const minFocusDistance = focus?.minFocusDistance?.value;
        const html = !minFocusDistance ? '' : `${minFocusDistance}cm`;
        return { id: product._id, value: minFocusDistance, html };
      },
      (val) => !!val,
      (values) => Math.min(...values),
      (val, best) => val === best
    );
  }

  private getMaxMagnificationItem(): SpecsItem {
    return this.createSpecsItem<number>(
      'ingrandimento massimo',
      (product) => {
        const focus = product?.cameraFixedLensData?.lensFocus || product.lensData.lensFocus;
        const maxMagnification = focus?.maxMagnification?.value;
        const html = !maxMagnification ? '' : `${maxMagnification}x`;
        return { id: product._id, value: maxMagnification, html };
      },
      (val) => !!val,
      (values) => Math.max(...values),
      (val, best) => val === best
    );
  }

  private getApertureMaxItem(): SpecsItem {
    return this.createSpecsItem<number>(
      'apertura massima diaframma',
      (product) => {
        const apertures = product.cameraFixedLensData?.aperture || product.lensData?.aperture;
        const aperturesMax = apertures?.max?.filter((v) => !!v) ?? [];
        const firstAperture = aperturesMax.at(0);
        const lastAperture = aperturesMax.at(-1);
        const isZoomLens = product.productData.productType === ProductTypeEnum.OBIETTIVO
          && product.lensData?.focals?.some(({ min, max }) => min !== max);
        const hasConstantAperture = aperturesMax.length === 1 || firstAperture === lastAperture;

        let html = '';
        if (firstAperture) {
          if (!isZoomLens) {
            html = `f/${firstAperture}${hasConstantAperture ? '' : `-${lastAperture}`}`;
          } else if (hasConstantAperture) {
            html = `f/${firstAperture} (apertura costante)`;
          } else {
            html = `da f/${firstAperture} ad f/${lastAperture} a seconda della focale`;
          }
        }
        return { id: product._id, value: aperturesMax?.at(0), html };
      },
      (val) => !!val,
      (values) => Math.min(...values),
      (val, best) => val === best
    );
  }

  private getApertureMinItem(): SpecsItem {
    return this.createSpecsItem<number[]>(
      'apertura minima diaframma',
      (product) => {
        const apertures = product.cameraFixedLensData?.aperture || product.lensData?.aperture;
        const aperturesMin = apertures?.min?.filter( (v) => !!v );
        const html = aperturesMin?.length ? `f/${aperturesMin.at(0)}${aperturesMin.length > 1 ? '-' + aperturesMin.at(1) : ''}` : '';
        return { id: product._id, value: aperturesMin, html };
      },
      (val) => !!val?.length
    );
  }

  private getFiltersDiameterItem(): SpecsItem {
    return this.createSpecsItem<number>(
      'diametro filtri',
      (product) => {
        const filtersDiameter = product.cameraFixedLensData?.filtersDiameter?.value || product.lensData?.filtersDiameter?.value;
        const html = filtersDiameter === 0 ? 'Non compatibile' : !filtersDiameter ? '' : `${filtersDiameter}mm`;
        return { id: product._id, value: filtersDiameter, html };
      },
      (val) => val !== undefined && val !== null,
      (values) => 1,
      (val, best) => !!val
    );
  }

  private getCameraConnectivityItem(key: keyof CameraConnectivityResponseDTO, label: string, skipIfUnknown = true): SpecsItem {
    return this.createSpecsItem<boolean>(
      label,
      ({ cameraData, _id }) => {
        const item = cameraData?.connectivity[key];
        const known = !!item;
        const supported = item?.hasIt;
        const html = !known ? '' : `${supported ? 'Sì' : 'No'} ${item.notes?.length ? ('(' + item.notes + ')') : ''}`;
        return { id: _id, value: supported, html };
      },
      (val) => val !== undefined,
      (values) => true,
      (val, best) => val === true
    );
  }

}
